import { useLazyQuery } from '@apollo/client'
import { useLocation, useQueryParams } from '@fs/zion-router'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import useDeepEqualMemoization from '../../../shared/hooks/useDeepEqualMemoization'
import ImageAuditContextProvider from '../../hooks/useImageAuditContext'

import {
  ErrorStatusProvider,
  GroupDataProvider,
  GroupWorkflowActionsProvider,
  ImageOrganizeProvider,
} from './providers'
import queries from './queries'
import BrowseProvider from './providers/BrowseProvider'

const BrowseProviders = ({ children }) => {
  const location = useLocation()
  // Memoize the query object to prevent unnecessary re-renders and infinite loops
  const { query: unmemoizedQuery, setQuery } = useQueryParams({ replaceState: true })
  const query = useDeepEqualMemoization(unmemoizedQuery)

  const [fetchParentId, { loading: loadingParentId, error: parentIdError }] = useLazyQuery(
    queries.naturalGroupParentId()
  )
  const [fetchRmsId, { error: rmsIdError }] = useLazyQuery(queries.getArtifactParent())
  const [fetchInactiveRmsId, { error: inactiveRmsIdError }] = useLazyQuery(queries.getArtifactParentWithInactive())
  const [fetchGroupData, { data: groupData, loading: loadingGroup, error: groupDataError }] = useLazyQuery(
    queries.getSimpleGroupDetails(),
    {
      errorPolicy: 'all',
    }
  )

  const [loadingNonGraph, setLoadingNonGraph] = useState(true)
  const validateRMSIdOnce = useRef(true)

  const loading = useMemo(() => {
    return loadingGroup || loadingParentId || loadingNonGraph
  }, [loadingGroup, loadingParentId, loadingNonGraph])

  const errorStatus = useMemo(() => {
    if (groupData?.group?.imageApids) return null // if there is an error but we have images we want to show the images
    return getGraphQLErrorStatus(groupDataError, rmsIdError, parentIdError)
  }, [groupData, groupDataError, parentIdError, rmsIdError])

  const formatSimpleGroup = useCallback((simpleData) => {
    if (!simpleData?.group) return { ...simpleData }

    const isParentGroup = Boolean(simpleData.group.parentGroupId || simpleData.group.types?.includes('VOLUME_SET'))
    if (!isParentGroup) {
      // Ensure placeInformation is preserved even for non-parent groups
      return {
        ...simpleData,
        group: {
          ...simpleData?.group,
        },
      }
    }

    const parentGroup = {
      ancestralHall: simpleData.group.ancestralHall,
      changeHistory: [],
      childCount: 0,
      childGroups: [],
      childNumber: null,
      clan: simpleData.group.clan,
      currentWorkflowActions: [],
      firstAncestor: simpleData.group.firstAncestor,
      generationPoem: simpleData.group.generationPoem,
      id: simpleData.group.parentGroupId || simpleData.group.id,
      imageApids: [],
      imageReprocessing: [],
      imageRework: {},
      imageViewerCoverages: [],
      indexedChildCount: 0,
      internalData: {},
      metadata: {},
      migrantAncestor: simpleData.group.migrantAncestor,
      naturalGroupId: simpleData.group.naturalGroupId?.split('_')[0],
      parentGroupId: null,
      placeInformation: simpleData?.group?.placeInformation,
      properties: simpleData.group.properties || [],
      siblingCount: null,
      types: simpleData.group.types || [],
      volumeSetTitle: simpleData.group.volumeSetTitle,
      volumeSetTotalVolumes: simpleData.group.volumeSetTotalVolumes,
      volumes: simpleData.group.volumes,
    }

    if (simpleData.group.types.includes('VOLUME_SET')) {
      parentGroup.childGroups = simpleData.group.childGroups.map((group) => {
        parentGroup.imageApids.push(...group.imageApids)
        return group
      })
    } else if (Array.isArray(simpleData.group.siblingIdsWithChildren)) {
      simpleData.group.siblingIdsWithChildren.forEach((sibling) => {
        const apidsToAdd = sibling.childIds || []
        parentGroup.imageApids.push(...apidsToAdd)
        parentGroup.childCount += apidsToAdd.length || 0

        // If a group is deprecated, the ID of the active group comes back in the siblings, so simply checking by the IDs fails
        // Checking the equality of the first image works for now, but if moving dividers causes issues we will need a deeper equality check
        let currentGroup = simpleData.group.id === sibling.parentId
        if (!simpleData.group.active) currentGroup = simpleData.group.imageApids[0] === sibling.childIds[0]

        parentGroup.childGroups.push(
          currentGroup
            ? simpleData.group
            : {
                childCount: apidsToAdd.length,
                id: sibling.parentId,
                imageApids: apidsToAdd,
                imageViewerCoverages: [],
                indexedChildCount: 0,
                internalData: {},
                languages: [],
                metadata: {},
                naturalGroupId: '',
                parentGroupId: simpleData.group.parentGroupId,
                siblingIdsWithChildren: [],
                title: null,
                volumes: null,
              }
        )
      })
    }

    return { group: parentGroup }
  }, [])

  const addedGroupData = useMemo(() => {
    if (loading) return null
    if (groupData) return formatSimpleGroup(groupData)
    return null
  }, [formatSimpleGroup, groupData, loading])

  const requestGroupData = useCallback(async () => {
    if (query.groupId) {
      if (!['index', 'fullText'].includes(query.view)) {
        setLoadingNonGraph(false)
        try {
          const parentIdData = await fetchParentId({ variables: { childGroupIdOrArkId: query.groupId } })
          let groupIdToFetch = parentIdData?.data?.naturalGroupParentId ?? query.groupId
          // If cat param is present, use parent group ID to show full group
          // Otherwise, in non-organize views, use the child group ID
          if (query.view !== 'organize' && !query.cat) groupIdToFetch = query.groupId

          fetchGroupData({
            variables: {
              groupIdOrArkId: groupIdToFetch,
              includePlaceInformation: true,
              isVolumeSet: Boolean(query.volumeSetSearch),
            },
          })
        } catch (e) {
          console.error(e)
        }
      } else {
        setLoadingNonGraph(true)
      }
    }
  }, [fetchGroupData, fetchParentId, query.groupId, query.view, query.volumeSetSearch, query.cat])

  // Checks to make sure that the ark matches the group that the image ark is in
  // We only want to run this once (when not a volumeSet page), otherwise causes unwanted side effects
  const validateRmsId = useCallback(async () => {
    try {
      // Since we are skipping when the view is index or fullText anyways, we will wait for the group to come back to run this code
      // so we can check if the group returned is active or not

      // I split these conditions so that we always skip this check on fullText or index view but only run it in explore view
      // when the right conditions are met
      if (['index', 'fullText'].includes(query.view)) return
      if (query.view !== 'explore' && (rmsIdError || inactiveRmsIdError || loadingGroup || !groupData)) return

      // Skip validation when cat param is present with a groupId - we're intentionally showing the full group
      // and the groupId might not match the child group that contains this specific image.
      // When groupId is absent, validation must still run so the correct group context is established.
      if (query.cat && query?.groupId?.length > 0) return

      if ((validateRMSIdOnce.current && !query.volumeSetSearch) || !query.groupId) {
        const ark = location.pathname.split('/').pop()
        const activeCheck = await fetchRmsId({ variables: { ark } })
        const inactiveCheck = await fetchInactiveRmsId({ variables: { ark } })

        const ids = [activeCheck?.data?.getArtifactParent, inactiveCheck?.data?.getArtifactParentWithInactive]
        if (!ids.includes(query.groupId)) setQuery({ groupId: activeCheck?.data?.getArtifactParent })
        validateRMSIdOnce.current = false
      }
    } catch (error) {
      console.error(error)
    }
  }, [
    query,
    rmsIdError,
    inactiveRmsIdError,
    loadingGroup,
    groupData,
    location.pathname,
    fetchRmsId,
    fetchInactiveRmsId,
    setQuery,
  ])

  useEffect(() => {
    validateRmsId()
  }, [validateRmsId])

  useEffect(() => {
    requestGroupData()
  }, [requestGroupData])

  return (
    <BrowseProvider>
      <GroupDataProvider initialData={addedGroupData}>
        <GroupWorkflowActionsProvider>
          <ImageOrganizeProvider parentGroup={groupData?.group}>
            <ImageAuditContextProvider>
              <ErrorStatusProvider errorStatusCode={errorStatus} isGroupError={!groupData?.group}>
                {children}
              </ErrorStatusProvider>
            </ImageAuditContextProvider>
          </ImageOrganizeProvider>
        </GroupWorkflowActionsProvider>
      </GroupDataProvider>
    </BrowseProvider>
  )
}

// returns the status code if there is an error
// returns null if there is no error
function getGraphQLErrorStatus(...errObjects) {
  return errObjects.reduce((prev, err) => {
    if (prev) return prev
    return err ? err?.graphQLErrors[0]?.extensions?.response?.status || 500 : null
  }, null)
}

export default BrowseProviders
